// @givmo/board — the proposals' tolerant decode and the small decisions every proposal surface shares
// (CBG-AI5-PROPOSE; backend `agent_docs/contracts/board-ai-proposals.md` §6).
//
// The rows are the generated `BoardMemberProposalOut`, `BoardPastRecordBatchSummaryOut` and
// `BoardPastRecordBatchOut`, read tolerantly: a malformed row is dropped, never thrown on. Everything
// the AI wrote (a name, a note, a record's text, a file's name) is untrusted text: the surfaces render
// it as plain text only, in `whitespace-pre-wrap` where it may hold lines (design §6, N4), and this
// package imports no Markdown renderer (`proposals.test.tsx` pins both).

import {
  firstErrorCode,
  isGivmoError,
  type BoardFileUploadSlotOut,
  type BoardMemberProposalOut,
  type BoardPastRecordBatchOut,
  type BoardPastRecordBatchSummaryOut,
  type BoardPastRecordProposalOut,
  type BoardProposalFileContentType,
} from '@givmo/api-client';
import { extractList } from '@givmo/api-client/list';
import { fileKindWord, proposalWords as w, type ProposedThrough } from './proposal-words';

export type MemberProposal = BoardMemberProposalOut;
export type PastRecordBatchSummary = BoardPastRecordBatchSummaryOut;
export type PastRecordBatch = BoardPastRecordBatchOut;
export type PastRecordProposal = BoardPastRecordProposalOut;

/** The one limit for a file, the AI's and a person's alike (design §3.3; Jonathan's answer 1). */
export const MAX_FILE_BYTES = 52_428_800;

/** What Board takes, as the file picker offers it: the types, and their extensions (a Word file
 *  since CBG-FIX160 H, D-CBG-47). */
export const FILE_ACCEPT =
  'application/pdf,image/jpeg,image/png,image/heic,image/tiff,application/msword,' +
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document,' +
  'application/vnd.ms-word.document.macroenabled.12,' +
  '.pdf,.jpg,.jpeg,.png,.heic,.tif,.tiff,.doc,.docx,.docm';

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isText(value: unknown): value is string {
  return typeof value === 'string';
}

/** The waiting proposed members, in the order served. */
export function decodeMemberProposals(body: unknown): MemberProposal[] {
  return extractList<MemberProposal>(body).filter(
    (row) =>
      isObject(row) &&
      isText(row.id) &&
      isText(row.source_note) &&
      isText(row.proposed_by_user_id) &&
      row.state === 'waiting',
  );
}

/**
 * The staged batches that hold something to review. A batch whose every record was left out stays
 * staged with nothing in it (the connection's next proposal joins it): it waits for no one.
 */
export function decodeWaitingBatches(body: unknown): PastRecordBatchSummary[] {
  return extractList<PastRecordBatchSummary>(body).filter(
    (row) =>
      isObject(row) &&
      isText(row.id) &&
      row.state === 'staged' &&
      typeof row.staged_count === 'number' &&
      row.staged_count > 0,
  );
}

/** One batch, or null for a shape this build can't read. */
export function decodeBatch(body: unknown): PastRecordBatch | null {
  if (!isObject(body) || !isText(body.id) || !isText(body.state)) return null;
  if (typeof body.revision !== 'number') return null;
  const proposals = Array.isArray(body.proposals) ? body.proposals : [];
  return {
    ...(body as PastRecordBatch),
    proposals: proposals.filter(
      (row): row is PastRecordProposal =>
        isObject(row) && isText(row.id) && isObject(row.file) && isText(row.file.state),
    ),
  };
}

/** The records still staged in a batch, in the order they were proposed. */
export function stagedRecords(batch: PastRecordBatch): PastRecordProposal[] {
  return (batch.proposals ?? [])
    .filter((row) => row.state === 'staged')
    .sort((left, right) => left.position - right.position);
}

/** Whether a staged record's file is there. Every other state reads as missing: one look. */
export function hasFile(record: PastRecordProposal): boolean {
  return record.file.state === 'attached';
}

/**
 * Whether a record goes in as a closed session's own (CBG-AI5-CLOSED). Read positively, as the
 * minute book reads a record's: a backend that predates the mark answers none, an ordinary record.
 */
export function isClosedSession(record: PastRecordProposal): boolean {
  return record.confidentiality === 'executive_session';
}

/** Whether a staged record names a file, there or not: a text it left out is then in that file. */
export function namesAFile(record: PastRecordProposal): boolean {
  return (record.file.file_name ?? '').trim().length > 0;
}

// ── CBG-FIX160 L (D-CBG-47; design-320 §8): a record's links to other records ─────────────────

/** Which link: the act that adopted the record, the meeting it is about, the version it replaces. */
export type LinkKind = 'adopted_by' | 'meeting' | 'supersedes';

/**
 * A link as the page says it: to a record proposed above (its title), to one in the minute book
 * (its title, or null where the reader isn't served it), or to a record the person left out, which
 * the confirmation drops (design §8.4).
 */
export type RecordLink =
  | { kind: LinkKind; where: 'proposed'; title: string }
  | { kind: LinkKind; where: 'book'; title: string | null }
  | { kind: LinkKind; where: 'left_out' };

const LINK_KINDS: readonly LinkKind[] = ['adopted_by', 'meeting', 'supersedes'];

function linkOf(
  record: PastRecordProposal,
  kind: LinkKind,
): { id: string | null; isProposal: boolean; title: string | null } {
  const title = (served: string | null | undefined) => (served ?? '').trim() || null;
  switch (kind) {
    case 'adopted_by':
      return {
        id: record.adopted_by_record_id ?? null,
        isProposal: record.adopted_by_is_proposal === true,
        title: title(record.adopted_by_title),
      };
    case 'meeting':
      return {
        id: record.meeting_record_id ?? null,
        isProposal: record.meeting_is_proposal === true,
        title: title(record.meeting_title),
      };
    case 'supersedes':
      return {
        id: record.supersedes_record_id ?? null,
        isProposal: record.supersedes_is_proposal === true,
        title: title(record.supersedes_title),
      };
  }
}

/** The proposals of the batch by id, every state. */
function byId(batch: PastRecordBatch): Map<string, PastRecordProposal> {
  return new Map((batch.proposals ?? []).map((row) => [row.id, row]));
}

/** A record's links, in the page's order: what adopted it, its meeting, the version it replaces. */
export function recordLinks(record: PastRecordProposal, batch: PastRecordBatch): RecordLink[] {
  const proposals = byId(batch);
  const links: RecordLink[] = [];
  for (const kind of LINK_KINDS) {
    const link = linkOf(record, kind);
    if (link.id === null) continue;
    if (!link.isProposal) {
      links.push({ kind, where: 'book', title: link.title });
      continue;
    }
    const target = proposals.get(link.id);
    if (target !== undefined && target.state === 'staged') {
      links.push({ kind, where: 'proposed', title: recordTitle(target) || link.title || '' });
    } else if (kind !== 'supersedes') {
      // A replaced version is never left out alone (the leave-out refuses it), so only an
      // "adopted by" or a meeting link can name a record left out.
      links.push({ kind, where: 'left_out' });
    }
  }
  return links;
}

/** The waiting records that name `record` as what adopted them, or as their meeting (§8.3). */
export function recordsNamingThis(
  record: PastRecordProposal,
  batch: PastRecordBatch,
): { id: string; kind: 'adopted_by' | 'meeting'; title: string }[] {
  const naming: { id: string; kind: 'adopted_by' | 'meeting'; title: string }[] = [];
  for (const other of stagedRecords(batch)) {
    for (const kind of ['adopted_by', 'meeting'] as const) {
      const link = linkOf(other, kind);
      if (link.isProposal && link.id === record.id) {
        naming.push({ id: other.id, kind, title: recordTitle(other) });
      }
    }
  }
  return naming;
}

/**
 * The waiting versions that replace `record`, newest first (§8.3): a replaced version is left out
 * only together with them, the newest first, so no newer version stands alone where its older one
 * was proposed. Empty for a record no waiting version replaces.
 */
export function versionsReplacing(
  record: PastRecordProposal,
  batch: PastRecordBatch,
): PastRecordProposal[] {
  const staged = stagedRecords(batch);
  const chain: PastRecordProposal[] = [];
  const seen = new Set<string>([record.id]);
  let current = record;
  for (;;) {
    const newer = staged.find((other) => {
      const link = linkOf(other, 'supersedes');
      return link.isProposal && link.id === current.id && !seen.has(other.id);
    });
    if (newer === undefined) break;
    seen.add(newer.id);
    chain.push(newer);
    current = newer;
  }
  return chain.reverse();
}

/** The waiting records that go in without a link they were proposed with, its record left out. */
export function goingInWithoutALink(batch: PastRecordBatch): PastRecordProposal[] {
  return stagedRecords(batch).filter((record) =>
    recordLinks(record, batch).some((link) => link.where === 'left_out'),
  );
}

// ── CBG-FIX160 G (D-CBG-47; design-320 §4): one signed file holding several records ────────────

/** How many other waiting records hold the same file: the same upload, or the same bytes. */
export function othersWithTheSameFile(record: PastRecordProposal, batch: PastRecordBatch): number {
  if (!hasFile(record)) return 0;
  const upload = record.file.inbox_file_id ?? null;
  const digest = record.file.sha256 ?? null;
  return stagedRecords(batch).filter(
    (other) =>
      other.id !== record.id &&
      hasFile(other) &&
      ((upload !== null && other.file.inbox_file_id === upload) ||
        (digest !== null && other.file.sha256 === digest)),
  ).length;
}

/**
 * Board's check turned a person's file down, in the page's own words (copy 4.4, 4.5), read off the
 * refusal's `error_code`, the kind the file was named as filling `{kind}`. Null for any other
 * refusal, which keeps the sentence Board served.
 */
export function fileCheckRefusal(
  error: unknown,
  contentType: string | null | undefined,
): string | null {
  if (!isGivmoError(error)) return null;
  switch (firstErrorCode(error)) {
    case 'wrong_type':
      return w.chosenWrongType(fileKindWord(contentType));
    case 'unreadable':
      return w.chosenUnreadable;
    case 'too_large':
      return w.chosenTooLarge;
    default:
      return null;
  }
}

/**
 * Whose connection it was: the reader's own (the words then say "your"), the person as the directory
 * names them, or someone it gives no name for (copy N5).
 */
export function proposedThrough(
  source: { proposed_by_user_id?: string | null; proposed_by_display_name?: string | null },
  viewerUserId: string | null | undefined,
): ProposedThrough {
  if (viewerUserId && source.proposed_by_user_id === viewerUserId) return { kind: 'yours' };
  const name = (source.proposed_by_display_name ?? '').trim();
  return name.length > 0 ? { kind: 'named', name } : { kind: 'unnamed' };
}

/** A staged record's title, as proposed (null on the wire only once a decision cleared its text). */
export function recordTitle(record: PastRecordProposal): string {
  return (record.title ?? '').trim();
}

/** Why a staged record's file isn't there, in one quiet sentence (copy 3.8–3.10); null when it is. */
export function missingReason(record: PastRecordProposal): string | null {
  const file = record.file;
  const name = (file.file_name ?? '').trim();
  switch (file.state) {
    case 'attached':
      return null;
    case 'awaiting_upload':
      return name.length > 0 ? w.fileNamed(name) : w.fileNotNamed;
    case 'too_large':
      return w.fileTooLarge(name);
    case 'refused':
      if (file.refused_reason === 'too_large') return w.fileTooLarge(name);
      if (file.refused_reason === 'wrong_type') {
        return w.fileWrongType(fileKindWord(file.content_type));
      }
      return w.fileUnreadable;
    default:
      return w.fileNotNamed;
  }
}

/**
 * How the console compares names: case, runs of spaces and Unicode compatibility forms ignored. A
 * proposal's title labels tick the board's titles they name this way (design §6.1).
 */
export function sameTitleName(left: string, right: string): boolean {
  const key = (name: string) =>
    name.normalize('NFKC').replace(/\s+/g, ' ').trim().toLocaleLowerCase('en-US');
  return key(left) === key(right);
}

const BY_EXTENSION: Record<string, BoardProposalFileContentType> = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  heic: 'image/heic',
  tif: 'image/tiff',
  tiff: 'image/tiff',
  // CBG-FIX160 H: a .docm's macros are removed by Board's check, which keeps it as a .docx.
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  docm: 'application/vnd.ms-word.document.macroenabled.12',
};

const ACCEPTED_TYPES = new Set<string>(Object.values(BY_EXTENSION));

/**
 * The type a chosen file is named as, from the browser's own reading or else its extension (a
 * browser often types a HEIC or TIFF as nothing); null for anything Board doesn't take. Board's
 * check reads the file's first bytes against it either way (design §3.3).
 */
export function chosenFileType(file: {
  name: string;
  type: string;
}): BoardProposalFileContentType | null {
  const type = file.type.toLowerCase();
  if (ACCEPTED_TYPES.has(type)) return type as BoardProposalFileContentType;
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  return BY_EXTENSION[extension] ?? null;
}

/**
 * PUT a file to a person's slot, sending exactly the headers the slot names: its signature binds them
 * (the content type and `Content-Disposition: attachment`), and a header left out reads as a 403.
 * The bytes go straight to storage, carrying no Givmo credential. True when storage took them.
 */
export async function putToSlot(slot: BoardFileUploadSlotOut, file: Blob): Promise<boolean> {
  const headers: Record<string, string> = {};
  for (const [name, value] of Object.entries(slot.headers ?? {})) {
    if (typeof value === 'string') headers[name] = value;
  }
  try {
    const stored = await fetch(slot.url, { method: slot.method ?? 'PUT', headers, body: file });
    return stored.ok;
  } catch {
    return false;
  }
}
